import BigNumber from 'bignumber.js';
import { fromWei, toWei } from 'helpers';
import { TokenData, VestingAccountData, VestingPoolVestingData } from 'models';
import { Contract } from 'web3-eth-contract';

export const isInVestingPool = async (
  connectedAddress: string,
  contract: Contract
): Promise<boolean> => {
  try {
    const accountData: VestingAccountData = await contract.methods
      .getAccountData(connectedAddress)
      .call();

    return new BigNumber(accountData.allocated).isGreaterThan(0);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);

    return false;
  }
};

// allocated - całkowita alokacja użytkownika
// claimed - całkowita kwota zclaimwoana (ale nie zdeponowana do gry)
// left - pozostała kwota alokacji (po uwzględnieniu zclaimowanej i zdeponowanej do gry)
// unlocked - kwota odblokowana od początku vestingu
// claimable - kwota możliwa do zclaimowania teraz
// - kwota którą można zclaimwoać na portfel (claim()) to: claimable
// - kwota którą można wypłacić do gry to: left
// - kwotę już zdeponowaną do gry można obliczyć jako: allocated - left - claimed

export const getVestingPoolData = async (
  connectedAddress: string,
  tokenData: TokenData,
  vestingPoolContract: Contract
): Promise<VestingPoolVestingData> => {
  const accountData: VestingAccountData = await vestingPoolContract.methods
    .getAccountData(connectedAddress)
    .call();

  // Allocated
  const amountAllocatedWei = accountData.allocated;
  const amountAllocated = fromWei(amountAllocatedWei, tokenData.decimals);

  // Claimable
  const amountClaimableWei = accountData.claimable;
  const amountClaimable = fromWei(amountClaimableWei, tokenData.decimals);

  // Claimed
  const amountClaimedWei = accountData.claimed;
  const amountClaimed = fromWei(amountClaimedWei, tokenData.decimals);

  // Left
  const amountLeftWei = accountData.left;
  const amountLeft = fromWei(amountLeftWei, tokenData.decimals);

  // Claimed to bridge
  const amountClaimedToBridgeWei = await vestingPoolContract.methods
    .claimedToBridge(connectedAddress)
    .call();
  const amountClaimedToBridge = fromWei(
    amountClaimedToBridgeWei,
    tokenData.decimals
  );

  // Total amount claimed
  const amountClaimedTotal = fromWei(
    BigNumber(amountClaimedWei)
      .plus(BigNumber(amountClaimedToBridgeWei))
      .toString(10),
    tokenData.decimals
  );

  return {
    amountAllocated,
    amountClaimable,
    amountLeft,
    amountClaimed,
    amountClaimedToBridge,
    amountClaimedTotal,
  };
};

export const postDepositFromVestingToGameBridge = async (
  vestingContract: Contract,
  depositValue: string,
  accountId: string,
  decimals: number,
  connectedAddress: string,
  serverId: number
): Promise<unknown> => {
  const depositValueWei = toWei(depositValue, decimals);

  // preflight check with "call" to return a possible error with reason message without posting transaction ("send" does not return a reason message in web3js 1.x)
  await vestingContract.methods
    .depositToBridge(serverId, depositValueWei, accountId)
    .call({
      from: connectedAddress,
      maxPriorityFeePerGas: null,
      maxFeePerGas: null,
    });

  return await vestingContract.methods
    .depositToBridge(serverId, depositValueWei, accountId)
    .send({
      from: connectedAddress,
      maxPriorityFeePerGas: null,
      maxFeePerGas: null,
    });
};
